import { useEffect, useState, useCallback, useRef } from 'react';

import { request, isCancel } from 'modules/Api/HttpClient';
import { DASHBOARD_EXAMS_IN_PROGRESS_URL } from 'modules/Api/Routes';

export const POLL_INTERVAL_MS = 60000;
const REQUEST_TIMEOUT_MS = 30000;

const INITIAL_STATE = {
  loading: true,
  loaded: false,
  hasData: false,
  error: null,
  exams: [],
  totalItems: null,
  generatedAt: null,
};

export const useExamsInProgressWidget = ({ enabled }) => {
  const [state, setState] = useState(INITIAL_STATE);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const hasDataRef = useRef(false);
  const retriedRef = useRef(false);

  const refetch = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setState((previous) => ({ ...previous, loading: true }));

    // skipServerError: a failed poll during a deploy must stay inside the widget.
    request({
      method: 'GET',
      url: DASHBOARD_EXAMS_IN_PROGRESS_URL,
      skipServerError: true,
      timeout: REQUEST_TIMEOUT_MS,
    })
      .then((response) => {
        inFlightRef.current = false;
        if (!mountedRef.current) return;
        const {
          exams = [],
          total_items: totalItems,
          generated_at: generatedAt,
        } = response.data.content;
        hasDataRef.current = true;
        setState({
          loading: false,
          loaded: true,
          hasData: true,
          error: null,
          exams,
          totalItems: totalItems ?? exams.length,
          generatedAt,
        });
      })
      .catch((error) => {
        inFlightRef.current = false;
        if (!mountedRef.current) return;
        if (isCancel(error)) {
          setState((previous) => ({ ...previous, loading: false }));
          // Other widgets call cancelRequests; don't leave the placeholder until the next poll.
          if (!hasDataRef.current && !retriedRef.current) {
            retriedRef.current = true;
            refetch();
          }
          return;
        }
        setState((previous) => ({
          ...previous,
          loading: false,
          loaded: true,
          error: error?.message || 'error',
        }));
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    let intervalId = null;
    const stop = () => {
      clearInterval(intervalId);
      intervalId = null;
    };
    const start = () => {
      if (intervalId !== null) return;
      refetch();
      intervalId = setInterval(refetch, POLL_INTERVAL_MS);
    };
    const handleVisibilityChange = () => (document.hidden ? stop() : start());

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stop();
    };
  }, [enabled, refetch]);

  return { state, refetch };
};
